import { memo } from "react";
import PropTypes from "prop-types";
import { formatInsightsDeltaDisplay } from "../utils/insightsSetRowMath";

/**
 * True when a row should show the colored up/down pill (not neutral “—”, not empty).
 * Neutral “—” is handled separately via `showNeutralDeltaBadge` on `InsightsMetricRow`.
 * @param {{ value: number, tone: string }|null|undefined} delta
 * @returns {boolean}
 */
export function insightsDeltaPillVisible(delta) {
  return Boolean(delta && delta.tone !== "neutral");
}

/**
 * True when any row carries a week-over-week delta object (up, down, or neutral “—”).
 * Used for `InsightsMetricUniformProvider` so the delta lane stays wide enough when every
 * change is neutral (directional `insightsDeltaPillVisible` alone would hide the column).
 * @param {ReadonlyArray<{ delta?: object | null | undefined }>} rows
 * @returns {boolean}
 */
export function insightsUniformRowsReserveDeltaLane(rows) {
  return rows.some((row) => row.delta != null);
}

/**
 * Pill showing week-over-week change vs the prior program week.
 * Purely presentational; parent supplies signed delta and semantic tone.
 */
function InsightsDeltaBadge({ delta, tone }) {
  if (tone === "neutral") {
    return (
      <span
        className="ProgramInsights-delta ProgramInsights-delta--neutral"
        aria-hidden="true"
      >
        —
      </span>
    );
  }

  const sign = delta > 0 ? "+" : "";
  const text = `${sign}${formatInsightsDeltaDisplay(delta)}`;

  return (
    <span
      className={`ProgramInsights-delta ProgramInsights-delta--${tone}`}
      aria-label={text}
    >
      {text}
    </span>
  );
}

InsightsDeltaBadge.propTypes = {
  delta: PropTypes.number.isRequired,
  tone: PropTypes.oneOf(["up", "down", "neutral"]).isRequired,
};

export default memo(InsightsDeltaBadge);
